import React, { useEffect, useMemo, useState } from "react";
import { BarChart3, Trophy, School as SchoolIcon, Loader2 } from "lucide-react";
import { api, BRL } from "@/lib/api";
import { Input } from "@/components/ui/input";
import PageHeader from "@/components/PageHeader";
import MonthPicker from "@/components/MonthPicker";
import { toast } from "sonner";

const THIS_MONTH = new Date().toISOString().slice(0, 7);
const THIS_YEAR = new Date().getFullYear();

export default function Relatorios() {
  const [tab, setTab] = useState("mensal");

  return (
    <div className="space-y-6">
      <PageHeader
        icon={BarChart3}
        title="Relatórios"
        subtitle="Visão consolidada por escola e ranking anual"
        iconColor="bg-[#7c3aed]"
      />

      <div className="flex gap-2 border-b border-slate-200">
        {[
          { k: "mensal", l: "Mensal por escola", icon: SchoolIcon },
          { k: "top",    l: "Top escolas",         icon: Trophy },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`rep-tab-${t.k}`}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors ${
              tab === t.k ? "border-[#7c3aed] text-[#7c3aed]" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.l}
          </button>
        ))}
      </div>

      {tab === "mensal" ? <MonthlyBySchool /> : <TopSchools />}
    </div>
  );
}

/* ---------------- Monthly by school ---------------- */
const MonthlyBySchool = () => {
  const [month, setMonth] = useState(THIS_MONTH);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/school-monthly?month=${month}`);
      setData(r.data);
    } catch (e) {
      toast.error("Falha ao carregar relatório");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [month]); // eslint-disable-line

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs font-bold text-slate-500 shrink-0">Mês:</label>
        <div className="flex-1 min-w-[200px]">
          <MonthPicker value={month} onChange={setMonth} testid="rep-month" />
        </div>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : !data || data.rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center text-sm text-slate-500">
          Sem movimentação registrada neste mês.
        </div>
      ) : (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Orçamentos" value={data.totals.budgets_count} sub={BRL(data.totals.budgets_total)} />
            <StatCard label="Pagos (NF)" value={data.totals.paid_count} sub={BRL(data.totals.paid_total)} color="text-emerald-700" />
            <StatCard label="FDP ativo" value={data.totals.fdp_count} sub={BRL(data.totals.fdp_total)} color="text-amber-700" />
            <StatCard label="Líquido" value={BRL(data.totals.net)} sub={`Impostos: ${BRL(data.totals.taxes)}`} color={data.totals.net >= 0 ? "text-[#1e3a8a]" : "text-rose-600"} emphasize />
          </div>

          {/* Per-school table */}
          <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Escola</th>
                  <th className="text-right py-3 px-4 font-semibold">Orçamentos</th>
                  <th className="text-right py-3 px-4 font-semibold">Pagos</th>
                  <th className="text-right py-3 px-4 font-semibold">FDP</th>
                  <th className="text-right py-3 px-4 font-semibold">Impostos</th>
                  <th className="text-right py-3 px-4 font-semibold">Despesas</th>
                  <th className="text-right py-3 px-4 font-semibold">Líquido</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.map((r) => (
                  <tr key={r.school_id} data-testid={`rep-school-${r.school_id}`} className="hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <p className="font-bold text-[#1e3a8a] text-sm leading-tight">{r.school_name}</p>
                      {r.city && <p className="text-[10px] text-slate-500">{r.city}</p>}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <p className="font-semibold text-slate-900">{r.budgets_count}</p>
                      <p className="text-[11px] text-slate-500">{BRL(r.budgets_total)}</p>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <p className="font-semibold text-emerald-700">{r.paid_count}</p>
                      <p className="text-[11px] text-slate-500">{BRL(r.paid_total)}</p>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <p className="font-semibold text-amber-700">{r.fdp_count}</p>
                      <p className="text-[11px] text-slate-500">{BRL(r.fdp_total)}</p>
                    </td>
                    <td className="py-3 px-4 text-right text-rose-600 font-semibold">{BRL(r.taxes)}</td>
                    <td className="py-3 px-4 text-right text-amber-700">{BRL(r.expenses_total)}</td>
                    <td className={`py-3 px-4 text-right font-black ${r.net >= 0 ? "text-[#1e3a8a]" : "text-rose-600"}`}>{BRL(r.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

/* ---------------- Top schools ---------------- */
const TopSchools = () => {
  const [year, setYear] = useState(THIS_YEAR);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/top-schools?year=${year}&limit=20`);
      setData(r.data);
    } catch (e) {
      toast.error("Falha ao carregar ranking");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [year]); // eslint-disable-line

  const maxPaid = useMemo(() => {
    const rows = data?.rows || [];
    return Math.max(1, ...rows.map((r) => r.paid_total));
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs font-bold text-slate-500 shrink-0">Ano:</label>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value) || THIS_YEAR)}
          data-testid="rep-year-filter"
          className="h-10 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 px-3 pr-8 focus:outline-none focus:ring-2 focus:ring-violet-400"
        >
          {(() => {
            const arr = [];
            for (let y = THIS_YEAR + 1; y >= THIS_YEAR - 10; y--) arr.push(y);
            return arr.map((y) => <option key={y} value={y}>{y}</option>);
          })()}
        </select>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : !data || data.rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center text-sm text-slate-500">
          Nenhum orçamento registrado em {year}.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" />
            <p className="font-heading font-black text-[#1e3a8a]">Ranking de escolas em {data.year}</p>
          </div>
          <ul className="divide-y divide-slate-100">
            {data.rows.map((r, i) => {
              const pct = (r.paid_total / maxPaid) * 100;
              const medal = i === 0 ? "bg-amber-400" : i === 1 ? "bg-slate-400" : i === 2 ? "bg-orange-400" : "bg-slate-200 text-slate-600";
              return (
                <li key={r.school_id} data-testid={`rank-${i}`} className="p-3 flex items-start gap-3">
                  <span className={`w-8 h-8 rounded-full ${medal} ${i<3?"text-white":""} flex items-center justify-center text-sm font-black shrink-0 mt-0.5`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#1e3a8a] text-[13px] leading-tight break-words line-clamp-2">{r.school_name}</p>
                    {r.city && <p className="text-[10px] text-slate-500 mt-0.5">{r.city}</p>}
                    <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#7c3aed] to-[#1e3a8a]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap sm:hidden">
                      <p className="text-[10px] text-slate-500">{r.paid_count} pago(s) · {r.budgets_count} orç.</p>
                      <p className="font-black text-[#1e3a8a] text-sm tabular-nums">{BRL(r.paid_total)}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0 hidden sm:block">
                    <p className="font-black text-[#1e3a8a]">{BRL(r.paid_total)}</p>
                    <p className="text-[10px] text-slate-500">{r.paid_count} pago(s) · {r.budgets_count} orç.</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

/* ---------------- Stat card ---------------- */
const StatCard = ({ label, value, sub, color = "text-slate-900", emphasize = false }) => (
  <div className={`rounded-2xl p-4 border bg-white card-shadow ${emphasize ? "border-[#7c3aed]/30 ring-2 ring-[#7c3aed]/10" : "border-slate-100"}`}>
    <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
    <p className={`mt-1 font-heading font-black text-2xl ${color}`}>{value}</p>
    {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
  </div>
);
